import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Button,
  Alert,
  Chip,
  Avatar,
  LinearProgress,
  Divider,
  IconButton,
  Badge,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import EmergencyChronometer from '../components/EmergencyChronometer';
import {
  Warning as EmergencyIcon,
  LocalHospital as HospitalIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Navigation as LocationIcon,
  CheckCircle as CheckIcon,
  Room as RoomIcon,
  MedicalServices as MedicalIcon,
  LocalPharmacy as PharmacyIcon,
  Assignment as AssignmentIcon,
  Timer as TimerIcon,
  AirportShuttle as AmbulanceIcon,
  Visibility as ViewIcon,
  Speed as SpeedIcon
} from '@mui/icons-material';

interface EmergencyCase {
  id: string;
  patientId: string;
  patientName: string;
  age: number;
  severity: 'stroke_suspected' | 'cardiac_event' | 'general_emergency';
  triggerTime: string;
  location: string;
  distance: string;
  estimatedArrival: string;
  status: 'incoming' | 'dispatched' | 'en_route' | 'arrived' | 'in_treatment' | 'completed';
  familyContact: string;
  familyPhone: string;
  medicalHistory: string[];
  currentSymptoms: string[];
  aiAssessment: {
    confidence: number;
    urgencyScore: number;
    recommendation: string;
  };
  preparation: {
    roomAssigned: boolean;
    doctorNotified: boolean;
    equipmentReady: boolean;
    medicationsPrepared: boolean;
    labAlerted: boolean;
    imagingReady: boolean;
  };
  assignedTeam: {
    doctor: string;
    nurse: string;
    room: string;
  };
}

const HospitalEmergencyDashboard: React.FC = () => {
  const [emergencyCases, setEmergencyCases] = useState<EmergencyCase[]>([
    {
      id: 'EMG001',
      patientId: '1',
      patientName: 'Juan Pérez',
      age: 68,
      severity: 'stroke_suspected',
      triggerTime: new Date(Date.now() - 8 * 60 * 1000).toISOString(), // 8 minutes ago
      location: 'Chillán Centro',
      distance: '2.3 km',
      estimatedArrival: '6 minutos',
      status: 'en_route',
      familyContact: 'María González (Hija)',
      familyPhone: '+56912345678',
      medicalHistory: ['Hipertensión', 'Diabetes', 'Historial de ACV'],
      currentSymptoms: ['Debilidad en brazo izquierdo', 'Dificultad para hablar', 'Confusión'],
      aiAssessment: {
        confidence: 0.92,
        urgencyScore: 85,
        recommendation: 'ACV SOSPECHOSO - Protocolo de emergencia neurológica'
      },
      preparation: {
        roomAssigned: true,
        doctorNotified: true,
        equipmentReady: false,
        medicationsPrepared: false,
        labAlerted: true,
        imagingReady: false
      },
      assignedTeam: {
        doctor: 'Dr. Roberto Silva (Neurólogo)',
        nurse: 'Enf. Carmen Rojas',
        room: 'Emergencia 3'
      }
    },
    {
      id: 'EMG002',
      patientId: '2',
      patientName: 'Rosa Martínez',
      age: 74,
      severity: 'cardiac_event',
      triggerTime: new Date(Date.now() - 3 * 60 * 1000).toISOString(), // 3 minutes ago
      location: 'Los Angeles',
      distance: '5.8 km',
      estimatedArrival: '12 minutos',
      status: 'dispatched',
      familyContact: 'Pedro Martínez (Hijo)',
      familyPhone: '+56987654321',
      medicalHistory: ['Fibrilación auricular', 'Marcapasos', 'Hipertensión'],
      currentSymptoms: ['Dolor de pecho severo', 'Sudoración', 'Náuseas'],
      aiAssessment: {
        confidence: 0.88,
        urgencyScore: 78,
        recommendation: 'EVENTO CARDÍACO - Protocolo de emergencia cardiológica'
      },
      preparation: {
        roomAssigned: true,
        doctorNotified: true,
        equipmentReady: true,
        medicationsPrepared: true,
        labAlerted: false,
        imagingReady: false
      },
      assignedTeam: {
        doctor: 'Dr. Ana López (Cardióloga)',
        nurse: 'Enf. Miguel Torres',
        room: 'Emergencia 1'
      }
    }
  ]);

  const [stats, setStats] = useState({
    activeCases: 2,
    averageResponseTime: 12,
    todayTotal: 8,
    roomsAvailable: 3,
    doctorsOnDuty: 4
  });

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'stroke_suspected': return 'error';
      case 'cardiac_event': return 'error';
      case 'general_emergency': return 'warning';
      default: return 'default';
    }
  };

  const getSeverityLabel = (severity: string) => {
    switch (severity) {
      case 'stroke_suspected': return 'ACV SOSPECHOSO';
      case 'cardiac_event': return 'EVENTO CARDÍACO';
      case 'general_emergency': return 'EMERGENCIA GENERAL';
      default: return severity;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'incoming': return 'info';
      case 'dispatched': return 'warning';
      case 'en_route': return 'warning';
      case 'arrived': return 'success';
      case 'in_treatment': return 'primary';
      case 'completed': return 'success';
      default: return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'incoming': return 'ENTRANTE';
      case 'dispatched': return 'DESPACHADO';
      case 'en_route': return 'EN RUTA';
      case 'arrived': return 'LLEGÓ';
      case 'in_treatment': return 'EN TRATAMIENTO';
      case 'completed': return 'COMPLETADO';
      default: return status;
    }
  };

  const getPreparationProgress = (preparation: any) => {
    const items = Object.values(preparation);
    const completed = items.filter(Boolean).length;
    return (completed / items.length) * 100;
  };

  const togglePreparationItem = (caseId: string, item: string) => {
    setEmergencyCases(cases => 
      cases.map(c => 
        c.id === caseId 
          ? { ...c, preparation: { ...c.preparation, [item]: !c.preparation[item as keyof typeof c.preparation] } }
          : c
      )
    );
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          Centro de Emergencias
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Chip icon={<EmergencyIcon />} label={`${stats.activeCases} Casos Activos`} color="error" />
          <Chip icon={<TimerIcon />} label={`${stats.averageResponseTime}min Promedio`} color="info" />
          <Chip icon={<RoomIcon />} label={`${stats.roomsAvailable} Salas Libres`} color="success" />
        </Box>
      </Box>

      {/* Quick Stats */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#ffebee' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <EmergencyIcon color="error" sx={{ fontSize: 40 }} />
              <Typography variant="h4" color="error">{stats.activeCases}</Typography>
              <Typography variant="body2">Emergencias Activas</Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#e3f2fd' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <SpeedIcon color="primary" sx={{ fontSize: 40 }} />
              <Typography variant="h4" color="primary">{stats.averageResponseTime}min</Typography>
              <Typography variant="body2">Tiempo Promedio</Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#e8f5e8' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <HospitalIcon color="success" sx={{ fontSize: 40 }} />
              <Typography variant="h4" color="success">{stats.todayTotal}</Typography>
              <Typography variant="body2">Casos Hoy</Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#fff3e0' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <PersonIcon color="warning" sx={{ fontSize: 40 }} />
              <Typography variant="h4" color="warning.main">{stats.doctorsOnDuty}</Typography>
              <Typography variant="body2">Médicos en Turno</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Emergency Cases */}
      <Grid container spacing={3}>
        {emergencyCases.map((emergencyCase) => (
          <Grid item xs={12} key={emergencyCase.id}>
            <Card 
              sx={{ 
                border: emergencyCase.severity === 'stroke_suspected' || emergencyCase.severity === 'cardiac_event' 
                  ? '3px solid #f44336' : '1px solid #e0e0e0',
                boxShadow: 3
              }}
            >
              <CardContent>
                <Grid container spacing={3}>
                  {/* Patient Info & Status */}
                  <Grid item xs={12} md={4}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Avatar sx={{ bgcolor: 'error.main', mr: 2, width: 56, height: 56 }}>
                        {emergencyCase.patientName.split(' ').map(n => n[0]).join('')}
                      </Avatar>
                      <Box>
                        <Typography variant="h6">{emergencyCase.patientName}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {emergencyCase.age} años • {emergencyCase.id}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                          <Chip 
                            label={getSeverityLabel(emergencyCase.severity)}
                            color={getSeverityColor(emergencyCase.severity) as any}
                            size="small"
                          />
                          <Chip 
                            label={getStatusLabel(emergencyCase.status)}
                            color={getStatusColor(emergencyCase.status) as any}
                            size="small"
                          />
                        </Box>
                      </Box>
                    </Box>

                    {/* Chronometer */}
                    <EmergencyChronometer 
                      startTime={emergencyCase.triggerTime}
                      variant="detailed"
                      size="medium"
                    />
                  </Grid>

                  {/* Medical Info */}
                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                      Síntomas Actuales:
                    </Typography>
                    <List dense>
                      {emergencyCase.currentSymptoms.map((symptom, index) => (
                        <ListItem key={index} sx={{ py: 0, px: 0 }}>
                          <ListItemIcon sx={{ minWidth: 20 }}>
                            <EmergencyIcon color="error" sx={{ fontSize: 16 }} />
                          </ListItemIcon>
                          <ListItemText primary={symptom} sx={{ my: 0 }} />
                        </ListItem>
                      ))}
                    </List>

                    <Typography variant="subtitle2" gutterBottom fontWeight="bold" sx={{ mt: 2 }}>
                      Historial Médico:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {emergencyCase.medicalHistory.map((condition, index) => (
                        <Chip key={index} label={condition} size="small" variant="outlined" />
                      ))}
                    </Box>

                    <Alert severity="error" sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" fontWeight="bold">
                        IA: {emergencyCase.aiAssessment.recommendation}
                      </Typography>
                      <Typography variant="caption">
                        Confianza: {(emergencyCase.aiAssessment.confidence * 100).toFixed(0)}% • 
                        Urgencia: {emergencyCase.aiAssessment.urgencyScore}/100
                      </Typography>
                    </Alert>
                  </Grid>

                  {/* Preparation & Team */}
                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                      Preparación del Hospital:
                    </Typography>
                    
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="body2">Progreso</Typography>
                        <Typography variant="body2">
                          {Math.round(getPreparationProgress(emergencyCase.preparation))}%
                        </Typography>
                      </Box>
                      <LinearProgress 
                        variant="determinate" 
                        value={getPreparationProgress(emergencyCase.preparation)}
                        color={getPreparationProgress(emergencyCase.preparation) === 100 ? 'success' : 'warning'}
                      />
                    </Box>

                    <List dense>
                      {Object.entries(emergencyCase.preparation).map(([key, completed]) => (
                        <ListItem key={key} sx={{ py: 0, px: 0 }}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={completed}
                                onChange={() => togglePreparationItem(emergencyCase.id, key)}
                                size="small"
                              />
                            }
                            label={
                              key === 'roomAssigned' ? 'Sala asignada' :
                              key === 'doctorNotified' ? 'Médico notificado' :
                              key === 'equipmentReady' ? 'Equipo preparado' :
                              key === 'medicationsPrepared' ? 'Medicamentos listos' :
                              key === 'labAlerted' ? 'Laboratorio alertado' :
                              key === 'imagingReady' ? 'Imágenes listas' : key
                            }
                            sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
                          />
                        </ListItem>
                      ))}
                    </List>

                    <Divider sx={{ my: 2 }} />

                    <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                      Equipo Asignado:
                    </Typography>
                    <List dense>
                      <ListItem sx={{ py: 0, px: 0 }}>
                        <ListItemIcon><PersonIcon /></ListItemIcon>
                        <ListItemText 
                          primary="Médico"
                          secondary={emergencyCase.assignedTeam.doctor}
                        />
                      </ListItem>
                      <ListItem sx={{ py: 0, px: 0 }}>
                        <ListItemIcon><PersonIcon /></ListItemIcon>
                        <ListItemText 
                          primary="Enfermera"
                          secondary={emergencyCase.assignedTeam.nurse}
                        />
                      </ListItem>
                      <ListItem sx={{ py: 0, px: 0 }}>
                        <ListItemIcon><RoomIcon /></ListItemIcon>
                        <ListItemText 
                          primary="Sala"
                          secondary={emergencyCase.assignedTeam.room}
                        />
                      </ListItem>
                    </List>

                    <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        startIcon={<PhoneIcon />}
                        onClick={() => window.open(`tel:${emergencyCase.familyPhone}`)}
                      >
                        Llamar Familia
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<ViewIcon />}
                      >
                        Ver Detalles
                      </Button>
                    </Box>
                  </Grid>
                </Grid>

                {/* Arrival Info */}
                {emergencyCase.status === 'en_route' && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <AmbulanceIcon />
                      <Typography fontWeight="bold">
                        Ambulancia en ruta desde {emergencyCase.location} • 
                        ETA: {emergencyCase.estimatedArrival} • 
                        Distancia: {emergencyCase.distance}
                      </Typography>
                    </Box>
                  </Alert>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* No active cases */}
      {emergencyCases.length === 0 && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <HospitalIcon sx={{ fontSize: 64, color: 'text.secondary' }} />
          <Typography variant="h6" color="text.secondary">
            No hay emergencias activas
          </Typography>
          <Typography variant="body2" color="text.secondary">
            El centro de emergencias está preparado para recibir nuevos casos
          </Typography>
        </Paper>
      )}
    </Container>
  );
};

export default HospitalEmergencyDashboard;